<template>
    <div class="faq">
        <div
            v-for="(item, index) in items"
            :key="index"
            class="question"
            :class="{ last: index === items.length - 1 }"
        >
            <div class="query" @click="toggle(index)">
                <span>{{ item.query }}</span>
                <div class="icon">
                    <IconMinus v-if="openIndex === index" />
                    <IconPlus v-else />
                </div>
            </div>
            <div v-if="openIndex === index" class="response">
                <template v-for="(segment, segmentIndex) in parseResponse(item.response)">
                    <b v-if="segment.bold" :key="`b-${segmentIndex}`">{{ segment.text }}</b>
                    <template v-else>{{ segment.text }}</template>
                </template>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
// Vue
import { ref } from 'vue'

// Components
import IconMinus from './IconMinus.vue'
import IconPlus from './IconPlus.vue'

// Types
export interface FaqItem {
    query: string
    response: string
}

// Props
const props = withDefaults(
    defineProps<{
        items: FaqItem[]
        // Index of the item open on first render (null for all closed)
        defaultOpenIndex?: number | null
    }>(),
    {
        defaultOpenIndex: 0,
    }
)

// State —> only one block open at a time
const openIndex = ref<number | null>(props.defaultOpenIndex)

function toggle(index: number) {
    openIndex.value = openIndex.value === index ? null : index
}

// Parse a response string into plain/bold segments —> anything wrapped in
// {curly braces} is rendered inside a <b> tag (matching the nested `b` styling
// in PreviewTakeover).
interface ResponseSegment {
    text: string
    bold: boolean
}

function parseResponse(response: string): ResponseSegment[] {
    const segments: ResponseSegment[] = []
    const regex = /\{([^}]*)\}/g
    let lastIndex = 0
    let match: RegExpExecArray | null

    while ((match = regex.exec(response)) !== null) {
        if (match.index > lastIndex) {
            segments.push({ text: response.slice(lastIndex, match.index), bold: false })
        }
        segments.push({ text: match[1], bold: true })
        lastIndex = regex.lastIndex
    }

    if (lastIndex < response.length) {
        segments.push({ text: response.slice(lastIndex), bold: false })
    }

    return segments
}
</script>

<style scoped lang="scss">
// Mixins
@mixin squircle($radius) {
    border-radius: $radius;

    @supports (corner-shape: superellipse(1.44)) {
        corner-shape: superellipse(1.44);
        border-radius: calc(#{$radius} * 1.36);
    }
}

.faq {
    position: relative;
    flex-shrink: 0;
}

.question {
    font-family: 'Inter Variable';
    font-feature-settings:
        'cv01' on,
        'cv11' on,
        'cv10' off,
        'ss01' off,
        'ss03' on;
    font-size: 12.37px;
    line-height: 20px;
    letter-spacing: 0;
    word-spacing: -0.2px;
    font-variation-settings:
        'wght' 436,
        'opsz' 14;
    color: white;
    padding-top: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid #19191a;
}
.question.last {
    border-bottom: none;
}

// Query
.question .query {
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    user-select: none;
    font-variation-settings:
        'wght' 500,
        'opsz' 14;
}
.question .query .icon {
    width: 10px;
    height: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.88;
}

// Response
.question .response {
    color: hsl(240, 3%, 60%);
    padding-top: 8px;
    padding-bottom: 6px;
}
.question .response b {
    background-color: hsl(240, 5%, 12%);
    @include squircle(5px);
    padding: 0 3px;
    font-variation-settings:
        'wght' 540,
        'opsz' 14;
}
</style>
